import { FC, ReactNode } from 'react'

import classnames from 'classnames'
import styles from './MediaThumb.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** The visual's file: an image, or with `kind="video"` the video itself, whose first frame shows */
  src?: string
  /** Text alternative of the visual; `''` for a decorative thumbnail whose context names it */
  alt: string
  /** What the visual is: `photo` (default), `video` or `pdf`; a video or a document carries a pill top-left */
  kind?: 'photo' | 'video' | 'pdf'
  /** Glyph of the kind pill, drawn at 1.2rem (e.g. a play icon) */
  kindIcon?: ReactNode
  /** Text of the kind pill, e.g. "PDF" or a duration */
  kindLabel?: string
  /** A video's still (its thumbnail): drawn as a lazy image instead of loading the video for its first frame */
  still?: string
  /** Side in px of a square thumbnail; without it the thumbnail fills its container's width at 4:3 */
  size?: number
  /** Additional class on the root, e.g. to size it per breakpoint */
  className?: string
}

/**
 * @component The rounded thumbnail of a visual, cover-fitted, with a pill saying what kind of media a video or a
 * document is. A placeholder gradient shows without a file. Renders as a `span`, so it may sit inside a button.
 * @example
 * <MediaThumb kind="video" src={`${video.url}#t=0.1`} alt="" kindIcon={<SmartDisplayOutlined />} size={68} />
 */
export const MediaThumb: FC<Props> = ({
  src,
  alt,
  kind = 'photo',
  kindIcon,
  kindLabel,
  still,
  size,
  className,
}) => {
  const hasPill = kind !== 'photo' && (kindIcon != null || !!kindLabel)

  return (
    <span
      className={classnames(styles.mediaThumb, className)}
      style={size !== undefined ? { width: size, height: size, aspectRatio: 'auto' } : undefined}
    >

      {hasPill &&
        <span className={styles.kind} data-kind={kind}>
          {kindIcon != null && <span className={styles.kindIcon} aria-hidden="true">{kindIcon}</span>}
          {kindLabel}
        </span>
      }

      {!!still && kind === 'video' && <img src={still} alt={alt} loading="lazy" />}

      {!still && !!src && kind === 'video' &&
        <video
          src={src}
          preload="metadata"
          muted
          playsInline
          {...(alt ? { role: 'img', 'aria-label': alt } : { 'aria-hidden': true })}
        />
      }

      {!!src && kind !== 'video' && <img src={src} alt={alt} loading="lazy" />}

    </span>
  )
}
